import _ from 'lodash'
import {
    type ContentSuggestionOptions,
    type EntitySuggestionResult,
    type OutlineSuggestionResult,
    type PageSuggestionResults,
    type LinkGenerationOutlineSuggestionResult,
    dataUtils
} from '@wix/document-manager-extensions'
import {getReportableFromError} from '@wix/document-manager-utils'
import type {
    Callback,
    Callback1,
    Callback2,
    PS,
    SerializedCompStructure,
    OutlineSuggestionResultForStructure,
    SectionSuggestionOnErrorParams,
    IdMap
} from '@wix/document-services-types'
import {deepClone} from '@wix/wix-immutable-proxy'
import routers from '../routers/routers'
import {VIEW_MODES} from '@wix/santa-core-utils/dist/ts/coreUtils/core/constants'

interface OutlineSuggestionOnSuccessParams {
    suggestionResults: OutlineSuggestionResult
}

interface OutlineSuggestionOnErrorParams {
    contentServerError?: string | object
    originalError: Error
    tokenUsage: any[]
}

interface LinkGenerationSuggestionOnSuccessParams {
    suggestionResults: LinkGenerationOutlineSuggestionResult
}

interface LinkGenerationOnErrorParams {
    originalError: Error
}

interface EntireSiteSuggestionOnErrorParams {
    originalError: Error
    pageSuggestions: PageSuggestionResults[]
}

async function getSuggestedOutline(
    ps: PS,
    pageId: string,
    category: string,
    businessName: string,
    description: string,
    onSuccess?: Callback1<OutlineSuggestionOnSuccessParams>,
    onError?: Callback1<OutlineSuggestionOnErrorParams>,
    options?: ContentSuggestionOptions,
    toneOfVoice?: string
) {
    try {
        const suggestion = await ps.extensionAPI.ai.content.getSuggestedOutline(
            pageId,
            category,
            businessName,
            description,
            options,
            toneOfVoice
        )
        ps.setOperationsQueue.asyncPreDataManipulationComplete(suggestion)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AIContentGetSuggestedOutlineError'
        })
        ps.setOperationsQueue.asyncPreDataManipulationComplete(null, newError as Error)

        ps.extensionAPI.logger.captureError(newError)

        onError?.({
            contentServerError: e?.extras?.responseBody,
            originalError: e,
            tokenUsage: e?.extras?.tokenUsage
        })
    }
}

function applyOutline(
    ps: PS,
    suggestionResults: OutlineSuggestionResult,
    pageId: string,
    category: string,
    businessName: string,
    description: string,
    onSuccess?: Callback1<OutlineSuggestionOnSuccessParams>,
    onError?: Callback1<OutlineSuggestionOnErrorParams>
) {
    try {
        ps.extensionAPI.ai.content.applyOutline(suggestionResults, pageId)
        onSuccess?.({
            suggestionResults
        })
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AIContentApplyOutlineError'
        })
        onError?.({
            originalError: e,
            tokenUsage: suggestionResults?.completionMetadata?.tokenUsage
        })
        ps.extensionAPI.logger.captureError(newError)
    }
}

const getRouterInnerRoutesWrapper = async (ps: PS, routerId: string, pageId: string): Promise<any> => {
    return new Promise(resolve => {
        routers.getRouterInnerRoutes(ps, routerId, pageId, resolve, false)
    })
}
const getDefaultDynamicPagesRoutes = async ps => {
    ps.extensionAPI.logger.interactionStarted('linkGenerationGetDynamicPages')
    let counter = 0
    const result = await ps.siteAPI.getAllPagesIds(false).reduce(async (previousPromise, pageId: string) => {
        const res = await previousPromise
        const pageRef = ps.pointers.getPointer(pageId, VIEW_MODES.DESKTOP)
        const routerRef = routers.getRouterRef.byPage(ps, pageRef)
        if (routerRef) {
            const {id} = routers.get.byRef(ps, routerRef)
            counter++
            const innerRoutes = await getRouterInnerRoutesWrapper(ps, id, pageId)
            if (innerRoutes.length > 0) {
                const firstInnerRouter = _.head(innerRoutes)
                res[pageId] = {
                    innerRoute: (firstInnerRouter as any).url,
                    routerId: id
                }
            }
        }
        return res
    }, Promise.resolve({}))
    ps.extensionAPI.logger.interactionEnded('linkGenerationGetDynamicPages', {counter})
    return result
}

async function uploadAndInjectImages(
    ps: PS,
    idMap: IdMap,
    data: Record<string, string>,
    oldUrlMap: Record<string, string>,
    onSuccess?: Callback,
    onError?: Callback1<any>
) {
    try {
        await ps.extensionAPI.ai.content.uploadAndInjectImages({idMap, data, oldUrlMap})
        onSuccess?.()
        ps.setOperationsQueue.asyncPreDataManipulationComplete()
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AIUploadAndInjectImagesError'
        })
        ps.extensionAPI.logger.captureError(newError)
        onError?.({
            contentServerError: e?.extras?.responseBody,
            originalError: e,
            tokenUsage: e?.extras?.tokenUsage
        })
    }
}

async function getPageLinksOutline(ps: PS, pageId: string, onSuccess?: Callback1<any>, onError?: Callback1<any>) {
    try {
        const defaultInnerRouterMap = await getDefaultDynamicPagesRoutes(ps)
        const pagesLinksOutlines = await ps.extensionAPI.ai.content.getSuggestedLinksToInject(pageId)
        pagesLinksOutlines.defaultInnerRouterMap = defaultInnerRouterMap

        ps.setOperationsQueue.asyncPreDataManipulationComplete(pagesLinksOutlines)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AIGetPossibleLinksOutlineError'
        })
        ps.setOperationsQueue.asyncPreDataManipulationComplete(null, newError as Error)

        ps.extensionAPI.logger.captureError(newError)

        onError?.({
            contentServerError: e?.extras?.responseBody,
            originalError: e,
            tokenUsage: e?.extras?.tokenUsage
        })
    }
}

async function getPagesLinksOutline(ps: PS, pageIds: string[], onSuccess?: Callback1<any>, onError?: Callback1<any>) {
    try {
        const defaultInnerRouterMap = await getDefaultDynamicPagesRoutes(ps)
        const pagesLinksOutlines = await Promise.all(
            pageIds.map(pageId => ps.extensionAPI.ai.content.getSuggestedLinksToInject(pageId))
        )
        ps.setOperationsQueue.asyncPreDataManipulationComplete({defaultInnerRouterMap, pagesLinksOutlines})
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AIGetPossibleLinksOutlineError'
        })
        ps.setOperationsQueue.asyncPreDataManipulationComplete(null, newError as Error)

        ps.extensionAPI.logger.captureError(newError)

        onError?.({
            contentServerError: e?.extras?.responseBody,
            originalError: e,
            tokenUsage: e?.extras?.tokenUsage
        })
    }
}

function generateLinksForOutline(
    ps: PS,
    pageLinksOutline: LinkGenerationOutlineSuggestionResult,
    pageId: string,
    onSuccess?: Callback1<LinkGenerationSuggestionOnSuccessParams>,
    onError?: Callback1<LinkGenerationOnErrorParams>
) {
    try {
        const {outline} = pageLinksOutline
        if (_.isEmpty(outline)) {
            onSuccess?.({
                suggestionResults: pageLinksOutline
            })
            return
        }
        ps.extensionAPI.ai.content.generateLinksForOutline(pageLinksOutline, pageId)
        onSuccess?.({
            suggestionResults: pageLinksOutline
        })
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AILinkGenerationCreationError'
        })
        onError?.({
            originalError: e
        })
        ps.extensionAPI.logger.captureError(newError)
    }
}

interface LinkGenerationResultWrapper {
    defaultInnerRouterMap: Record<string, any>
    pagesLinksOutlines: LinkGenerationOutlineSuggestionResult[]
}

function generatesLinksForOutline(
    ps: PS,
    res: LinkGenerationResultWrapper,
    pageIds,
    onSuccess?: Callback1<LinkGenerationSuggestionOnSuccessParams[]>,
    onError?: Callback1<LinkGenerationOnErrorParams[]>
) {
    const {defaultInnerRouterMap, pagesLinksOutlines} = res
    Promise.all(
        pagesLinksOutlines.map(pageLinkOutline => {
            return new Promise((resolve, reject) => {
                pageLinkOutline.defaultInnerRouterMap = defaultInnerRouterMap
                generateLinksForOutline(ps, pageLinkOutline, pageLinkOutline.pageId, resolve, reject)
            })
        })
    )
        // @ts-ignore
        // eslint-disable-next-line promise/prefer-await-to-then
        .then(onSuccess)
        // eslint-disable-next-line promise/prefer-await-to-then
        .catch(onError)
}

async function getSuggestedSiteOutlines(
    ps: PS,
    category: string,
    businessName: string,
    description: string,
    onSuccess?: Callback1<PageSuggestionResults[]>,
    onError?: Callback1<EntireSiteSuggestionOnErrorParams>,
    options?: ContentSuggestionOptions,
    toneOfVoice?: string
) {
    try {
        const suggestions = await ps.extensionAPI.ai.content.getSuggestedSiteOutlines(
            category,
            businessName,
            description,
            options,
            toneOfVoice
        )
        ps.setOperationsQueue.asyncPreDataManipulationComplete(suggestions)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AIContentgetSuggestedSiteOutlinesError'
        })
        ps.setOperationsQueue.asyncPreDataManipulationComplete(null, newError as Error)

        onError?.({
            originalError: e,
            pageSuggestions: e?.extras?.pageSuggestionResults
        })
        ps.extensionAPI.logger.captureError(newError)
    }
}

async function getSuggestedOutlineByStructure(
    ps: PS,
    category: string,
    businessName: string,
    description: string,
    compStructure: SerializedCompStructure,
    sectionCategory?: string,
    onSuccess?: Callback2<SerializedCompStructure, OutlineSuggestionResultForStructure>,
    onError?: Callback1<SectionSuggestionOnErrorParams>,
    language?: string, //should be string of the language for example English,
    outlineCompIdToSectionNameOverrides: Record<string, string> = {}
) {
    try {
        const sectionPromptVersionOverride = ps.siteAPI.getQueryParam('sectionPromptVersionOverride')
        const sectionParamsVersionOverride = ps.siteAPI.getQueryParam('sectionParamsVersionOverride')
        const suggestionResults = await ps.extensionAPI.ai.content.getSuggestedOutlineByStructure(
            category,
            businessName,
            description,
            compStructure,
            sectionCategory,
            {promptVersionOverride: sectionPromptVersionOverride, paramsVersionOverride: sectionParamsVersionOverride},
            language,
            outlineCompIdToSectionNameOverrides
        )

        ps.extensionAPI.ai.content.applyOutlineToStructure(
            suggestionResults.contentSuggestion,
            suggestionResults.compStructure
        )
        onSuccess?.(suggestionResults.compStructure, suggestionResults.contentSuggestion)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'getSuggestedOutlinesByStructure'
        })

        onError?.({
            originalError: e
        })
        ps.extensionAPI.logger.captureError(newError)
    }
}

async function getSuggestedOutlineByPageStructure(
    ps: PS,
    category: string,
    businessName: string,
    description: string,
    compStructure: SerializedCompStructure,
    onSuccess?: Callback1<EntitySuggestionResult>,
    onError?: Callback1<SectionSuggestionOnErrorParams>,
    language?: string, //should be string of the language for example English
    outlineCompIdToSectionNameOverrides: Record<string, string> = {}
) {
    try {
        const pagePromptVersionOverride = ps.siteAPI.getQueryParam('pagePromptVersionOverride')
        const pageParamsVersionOverride = ps.siteAPI.getQueryParam('pageParamsVersionOverride')
        const suggestionResults = await ps.extensionAPI.ai.content.getSuggestedOutlineByPageStructure(
            category,
            businessName,
            description,
            compStructure,
            {promptVersionOverride: pagePromptVersionOverride, paramsVersionOverride: pageParamsVersionOverride},
            language,
            outlineCompIdToSectionNameOverrides
        )
        ps.setOperationsQueue.asyncPreDataManipulationComplete({suggestionResults, compStructure})
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'getSuggestedOutlinesByStructure'
        })
        ps.setOperationsQueue.asyncPreDataManipulationComplete(null, newError as Error)

        onError?.({
            originalError: e
        })
        ps.extensionAPI.logger.captureError(newError)
    }
}

interface SerializedSectionParams {
    section: SerializedCompStructure
    sectionName?: string
}

const getSectionIdToNameMap = (compStructures: SerializedSectionParams[]) => {
    const idToNameMap: Record<string, string> = {}
    const generatedIds = []
    for (const {section, sectionName} of compStructures) {
        if (sectionName) {
            if (!section.id) {
                const newId = dataUtils.generateItemIdWithPrefix('comp')
                section.id = newId
                generatedIds.push(newId)
            }
            idToNameMap[section.id] = sectionName
        }
    }

    return {idToNameMap, generatedIds}
}

const safeGetJsonLength = (obj: any) => {
    try {
        return JSON.stringify(obj).length
    } catch {
        return -1
    }
}

async function getSuggestedOutlineByStructures(
    ps: PS,
    category: string,
    businessName: string,
    description: string,
    serializedSectionParams: SerializedSectionParams[],
    onSuccess?: Callback2<SerializedCompStructure[], OutlineSuggestionResultForStructure>,
    onError?: Callback1<SectionSuggestionOnErrorParams>,
    language?: string //should be string of the language for example English
) {
    try {
        // this function can mutate the serializedSections, we clone it here to prevent unexpected behavior
        serializedSectionParams = deepClone(serializedSectionParams)

        const mergedStructure: Partial<SerializedCompStructure> = {
            componentType: 'mobile.core.components.Page',
            components: serializedSectionParams.map(({section}) => section)
        }

        const {idToNameMap, generatedIds} = getSectionIdToNameMap(serializedSectionParams)

        const pagePromptVersionOverride = ps.siteAPI.getQueryParam('pagePromptVersionOverride')
        const pageParamsVersionOverride = ps.siteAPI.getQueryParam('pageParamsVersionOverride')
        const suggestionResults = await ps.extensionAPI.ai.content.getSuggestedOutlineByPageStructure(
            category,
            businessName,
            description,
            mergedStructure,
            {promptVersionOverride: pagePromptVersionOverride, paramsVersionOverride: pageParamsVersionOverride},
            language,
            idToNameMap
        )

        if (suggestionResults.error) {
            throw suggestionResults.error
        }

        // sections ids may have been generated for outline creation, we need to omit these
        suggestionResults.contentSuggestion.idsToKeep = _.omit(
            suggestionResults.contentSuggestion.idsToKeep,
            generatedIds
        )

        ps.extensionAPI.ai.content.applyOutlineToPageStructure(
            suggestionResults.contentSuggestion,
            suggestionResults.compStructure
        )
        onSuccess?.(suggestionResults.compStructure.components, suggestionResults.contentSuggestion)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'getSuggestedOutlineByStructures',
            extras: {
                contentLength: safeGetJsonLength(serializedSectionParams)
            }
        })
        ps.extensionAPI.logger.captureError(newError)

        onError?.({
            originalError: e
        })
    }
}

async function applyOutlines(
    ps: PS,
    suggestionResults: PageSuggestionResults[],
    category: string,
    businessName: string,
    description: string,
    onSuccess?: Callback1<PageSuggestionResults[]>,
    onError?: Callback1<EntireSiteSuggestionOnErrorParams>
) {
    try {
        for (const pageResult of suggestionResults.filter(pageSuggestion => !pageSuggestion?.error)) {
            ps.extensionAPI.ai.content.applyOutline(pageResult.pageSuggestion, pageResult.pageId)
        }
        onSuccess?.(suggestionResults)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AIContentApplyOutlinesForEntireSiteError'
        })
        onError?.({
            originalError: e,
            pageSuggestions: e?.extras?.pageSuggestionResults
        })
        ps.extensionAPI.logger.captureError(newError)
    }
}

async function applyOutlineToPage(
    ps: PS,
    suggestionData: {suggestionResults: EntitySuggestionResult},
    category: string,
    businessName: string,
    description: string,
    _compStructure: SerializedCompStructure,
    sectionCategory?: string,
    onSuccess?: Callback1<OutlineSuggestionResultForStructure>,
    onError?: Callback1<OutlineSuggestionOnErrorParams>
) {
    try {
        ps.extensionAPI.ai.content.applyOutline(
            suggestionData.suggestionResults.contentSuggestion,
            suggestionData.suggestionResults.entityId
        )
        onSuccess?.(suggestionData.suggestionResults.contentSuggestion)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AIContentApplyOutlineError'
        })
        onError?.({
            originalError: e,
            tokenUsage: suggestionData.suggestionResults.contentSuggestion?.completionMetadata?.tokenUsage
        })
        ps.extensionAPI.logger.captureError(newError)
    }
}

async function applyOutlineToPageStructure(
    ps: PS,
    suggestionData: {suggestionResults: EntitySuggestionResult; compStructure: SerializedCompStructure},
    category: string,
    businessName: string,
    description: string,
    _compStructure: SerializedCompStructure,
    onSuccess?: Callback2<SerializedCompStructure, OutlineSuggestionResultForStructure>,
    onError?: Callback1<SectionSuggestionOnErrorParams>
) {
    try {
        const {suggestionResults, compStructure} = suggestionData
        ps.extensionAPI.ai.content.applyOutlineToPageStructure(suggestionResults.contentSuggestion, compStructure)
        onSuccess?.(compStructure, suggestionResults.contentSuggestion)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'applyOutlinesToStructureError'
        })
        onError?.({
            originalError: e
        })
        ps.extensionAPI.logger.captureError(newError)
    }
}

const api = {
    getSuggestedOutline,
    applyOutline,
    getPageLinksOutline,
    getPagesLinksOutline,
    generateLinksForOutline,
    generatesLinksForOutline,
    getSuggestedSiteOutlines,
    applyOutlines,
    getSuggestedOutlineByStructure,
    getSuggestedOutlineByPageStructure,
    applyOutlineToPageStructure,
    applyOutlineToPage,
    getSuggestedOutlineByStructures,
    uploadAndInjectImages
}

export default api
